import type { CrossCheckFinding } from '../validation/crossCheckEngine';

/**
 * Provenance du parcours affiché dans le bandeau.
 *
 * Historique : le parcours était rendu comme une simple chaîne, sans dire d'où
 * il venait. Sur GD-567-EJ (#13374) l'agence avait coché « Ce dossier est une
 * succession » à l'étape 1 alors que l'analyse concluait `classique` : la
 * review affichait « Succession » sans aucun moyen de savoir que c'était une
 * DÉCLARATION de l'agence et pas une DÉTECTION de l'IA. On trace donc la
 * source à côté du libellé, des deux côtés (form + review).
 *
 *  - `agence` : case cochée à l'étape 1 (PlateAndDepartmentScreen) — prioritaire
 *    sur les moteurs, par design.
 *  - `v1`     : moteur CTVO v1 (`dossier.parcours`).
 *  - `opus`   : audit batch (`transaction.parcours`).
 *  - `regex`  : déduction déterministe (finding Parcours A / récépissé DA déposé).
 */
export type ParcoursSource = 'agence' | 'v1' | 'opus' | 'regex';

export interface DetectedParcours {
  label: string | null;
  source: ParcoursSource | null;
}

/** Phrase de provenance affichée sous le libellé du bandeau. */
export const PARCOURS_SOURCE_DETAILS: Record<ParcoursSource, string> = {
  agence: "Déclaré par l'agence à l'étape 1 du formulaire (case cochée), pas détecté par l'analyse des pièces",
  v1: 'Déterminé par l\'analyse automatique des pièces du dossier',
  opus: 'Déterminé par l\'audit automatique des pièces du dossier',
  regex: 'Déduit des pièces déposées (règles déterministes)',
};

/**
 * Libellés produits par la déclaration agence (priorité 0). Volontairement
 * distincts de ceux des moteurs : ils servent aussi de signature pour retrouver
 * la provenance des snapshots ANTÉRIEURS à l'ajout de `parcoursSource`
 * (cf. `inferParcoursSourceFromLabel`), sans backfill en base.
 */
export const AGENCY_DECLARED_PARCOURS_LABELS = {
  successionEtRachatPro: 'Succession + rachat professionnel (Parcours A) — pièces spécifiques requises',
  succession: 'Succession — pièces spécifiques requises (acte de décès + justificatif de succession)',
  rachatPro: "Parcours A — rachat par un professionnel (récépissé de déclaration d'achat requis)",
} as const;

/** Libellé du fallback déterministe (finding Parcours A / récépissé DA présent). */
export const REGEX_PARCOURS_A_LABEL =
  "Parcours A — cession par un professionnel intermédiaire (récépissé de déclaration d'achat requis)";

/** Libellés de l'audit batch (Opus), indexés par valeur de `transaction.parcours`. */
export const OPUS_PARCOURS_LABELS: Record<string, string> = {
  parcours_a: REGEX_PARCOURS_A_LABEL,
  succession: 'Succession — pièces spécifiques requises',
  autre: 'Parcours spécifique détecté',
};

export interface DetectParcoursInput {
  /** Case « Ce dossier est une succession » cochée à l'étape 1. */
  isSuccession: boolean;
  /** Case « Ce dossier a été racheté par un professionnel » cochée à l'étape 1. */
  isProfessionalResale: boolean;
  /** Libellé issu du moteur v1, ou `null` si indisponible / parcours classique. */
  v1Label: string | null;
  /** `transaction.parcours` de l'audit batch, ou `null` si indisponible / classique. */
  opusParcours: string | null;
  /** Un finding `parcours_a_*` s'est déclenché. */
  hasParcoursAFinding: boolean;
  /** Un récépissé de déclaration d'achat est présent au dossier. */
  hasRecepisseDa: boolean;
}

/**
 * Détermine le parcours affiché ET sa provenance. Logique extraite telle quelle
 * de `FormCGIAv2.detectedParcours` pour être partagée avec la review et,
 * surtout, testable (elle ne l'était pas).
 *
 * L'ordre de priorité est INCHANGÉ : déclaration agence > v1 > Opus > regex.
 */
export const detectParcours = ({
  isSuccession,
  isProfessionalResale,
  v1Label,
  opusParcours,
  hasParcoursAFinding,
  hasRecepisseDa,
}: DetectParcoursInput): DetectedParcours => {
  // Priorité 0 : déclaration explicite de l'agence à l'étape 1. C'est le sens
  // de la feature — l'agence déclare succession / rachat pro plutôt que de
  // laisser l'IA le deviner, donc cette intention prime sur la détection.
  if (isSuccession && isProfessionalResale) {
    return { label: AGENCY_DECLARED_PARCOURS_LABELS.successionEtRachatPro, source: 'agence' };
  }
  if (isSuccession) {
    return { label: AGENCY_DECLARED_PARCOURS_LABELS.succession, source: 'agence' };
  }
  if (isProfessionalResale) {
    return { label: AGENCY_DECLARED_PARCOURS_LABELS.rachatPro, source: 'agence' };
  }
  // Priorité 1 : parcours déterminé par le moteur v1 (dossier.parcours).
  if (v1Label) return { label: v1Label, source: 'v1' };
  // Priorité 2 : parcours déterminé par l'audit Opus (champ transaction.parcours).
  if (opusParcours && opusParcours !== 'classique') {
    return { label: OPUS_PARCOURS_LABELS[opusParcours] ?? `Parcours : ${opusParcours}`, source: 'opus' };
  }
  // Fallback : détection depuis les findings regex / présence d'un récépissé DA.
  if (hasParcoursAFinding || hasRecepisseDa) {
    return { label: REGEX_PARCOURS_A_LABEL, source: 'regex' };
  }
  return { label: null, source: null };
};

const AGENCY_LABEL_SET = new Set<string>(Object.values(AGENCY_DECLARED_PARCOURS_LABELS));

/**
 * Retrouve la provenance d'un parcours à partir du seul libellé, pour les
 * snapshots figés AVANT l'ajout de `parcoursSource` (dossiers déjà soumis).
 * Les trois libellés de la déclaration agence sont uniques dans le produit :
 * un moteur ne peut pas les émettre. Tout le reste est une détection, dont on
 * ne peut pas dire quel moteur l'a produite → `null` (aucune ligne de
 * provenance affichée, plutôt qu'une provenance inventée).
 */
export const inferParcoursSourceFromLabel = (label: string | null | undefined): ParcoursSource | null =>
  label && AGENCY_LABEL_SET.has(label) ? 'agence' : null;

export const SUCCESSION_DECLARATION_MISMATCH_RULE_ID = 'succession_declaree_sans_element';

export interface SuccessionDeclarationInput {
  /** Case « Ce dossier est une succession » cochée à l'étape 1. */
  isSuccession: boolean;
  /** `dossier.parcours` du moteur v1, ou `null` si le moteur n'a pas tourné. */
  v1Parcours: string | null;
  /** `transaction.parcours` de l'audit batch, ou `null` s'il n'a pas tourné. */
  opusParcours: string | null;
  /** Au moins une pièce `acte_deces` ou `justif_succession` est présente au dossier. */
  hasSuccessionDoc: boolean;
}

/**
 * Contradiction « succession déclarée mais rien qui la corrobore ».
 *
 * Cas réel GD-567-EJ (#13374) : case cochée, analyse `classique` (cédant
 * particulier → particulier), zéro pièce de succession déposée. Le dossier
 * réclamait 2 pièces fantômes (7/9) et personne n'était alerté de l'écart.
 * L'agence voit cette alerte AVANT de soumettre, donc peut se corriger seule.
 *
 * Silencieuse quand :
 *  - la case n'est pas cochée ;
 *  - une pièce de succession est présente (déclaration corroborée) ;
 *  - un moteur confirme la succession ;
 *  - aucun moteur n'a encore rendu de verdict (on n'accuse pas dans le vide).
 */
export const buildSuccessionDeclarationMismatchFinding = ({
  isSuccession,
  v1Parcours,
  opusParcours,
  hasSuccessionDoc,
}: SuccessionDeclarationInput): CrossCheckFinding | null => {
  if (!isSuccession || hasSuccessionDoc) return null;
  const verdicts = [v1Parcours, opusParcours].filter((p): p is string => Boolean(p));
  if (verdicts.length === 0) return null;
  if (verdicts.includes('succession')) return null;
  return {
    ruleId: SUCCESSION_DECLARATION_MISMATCH_RULE_ID,
    severity: 'warning',
    message:
      "Ce dossier est déclaré « succession » (case cochée à l'étape 1) mais l'analyse des pièces conclut à une " +
      "cession classique, et ni acte de décès ni justificatif de succession n'a été déposé. Si ce n'est pas une " +
      "succession, décochez la case pour retirer ces deux pièces de la liste ; sinon, déposez l'acte de décès et " +
      'le justificatif de succession.',
    documentTypes: ['acte_deces', 'justif_succession'],
  };
};
